import { IonCard, IonCardContent, IonIcon } from '@ionic/react';
import { Box, Typography, Avatar, IconButton, Modal, Fade, Tooltip, CircularProgress, Chip } from '@mui/material';
import { heart, heartOutline, expand } from 'ionicons/icons';
import { 
  Business as BusinessIcon, 
  Person as PersonIcon
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { Post } from '../types/post';
import { useState, useEffect } from 'react';
import { doc, updateDoc, increment, addDoc, collection, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

interface PostCardProps {
  post: Post;
  currentUserId?: string;
}

export const PostCard: React.FC<PostCardProps> = ({ post, currentUserId }) => {
  const [profileImageLoaded, setProfileImageLoaded] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState<{[key: number]: boolean}>({});
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [authorType, setAuthorType] = useState<string | null>(null);
  const [authorSpecialization, setAuthorSpecialization] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  
  // Zoom functionality state
  const [zoomLevel, setZoomLevel] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const { t } = useTranslation();

  useEffect(() => {
    // Delay rendering of Ionic components until after initial mount
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const fetchAuthorInfo = async () => {
      try {
        const authorDoc = await getDoc(doc(db, 'users', post.authorId));
        if (authorDoc.exists()) {
          const authorData = authorDoc.data();
          setAuthorType(authorData.type || null);
          
          // Get specialization based on user type
          if (authorData.type === 'company' || authorData.type === 'contractor') {
            setAuthorSpecialization(authorData.specialization || null);
          } else if (authorData.type === 'ambassador') {
            // For ambassador, get the activity type as specialization
            if (authorData.activityType) {
              // Handle both string and CustomField object
              const activityType = typeof authorData.activityType === 'string'
                ? authorData.activityType
                : authorData.activityType.isOther
                  ? (authorData.activityType.customValue || '')
                  : authorData.activityType.value;
              
              // Capitalize first letter
              setAuthorSpecialization(
                activityType ? activityType.charAt(0).toUpperCase() + activityType.slice(1) : null
              );
            }
          }
        }
      } catch (error) {
        console.error('Error fetching author info:', error);
      }
    };
    fetchAuthorInfo();
  }, [post.authorId]);
  const history = useHistory();

  const navigateToProfile = () => {
    history.push(`/profile/${post.authorId}`);
  };

  const [isLiked, setIsLiked] = useState(post.likedBy ? post.likedBy.includes(currentUserId || '') : false);
  const [likeCount, setLikeCount] = useState(post.likes || 0);

  const handleLike = async () => {
    try {
      const postRef = doc(db, 'posts', post.id);
      
      if (!currentUserId) return;

      // Check if user has already liked the post
      if (isLiked) {
        // User is unliking the post
        const newLikedBy = (post.likedBy || []).filter(id => id !== currentUserId);
        
        await updateDoc(postRef, {
          likes: increment(-1),
          likedBy: newLikedBy
        });
      } else {
        // User is liking the post
        // Check if user already exists in likedBy array to enforce one-like-per-user limit
        if (post.likedBy && post.likedBy.includes(currentUserId)) {
          console.log('User has already liked this post');
          return;
        }
        
        const newLikedBy = [...(post.likedBy || []), currentUserId];
        
        await updateDoc(postRef, {
          likes: increment(1),
          likedBy: newLikedBy
        });
      }

      // Create notification when liking (not when unliking)
      if (!isLiked && post.authorId !== currentUserId) {
        // Check notification settings
        const settingsDoc = await getDoc(doc(db, 'notificationSettings', post.authorId));
        const settings = settingsDoc.exists() ? settingsDoc.data() : { likes: true };
        
        if (settings.likes) {
          // Get current user's name
          const userDoc = await getDoc(doc(db, 'users', currentUserId));
          const userData = userDoc.data();
          const userName = userData?.firstName 
            ? `${userData.firstName} ${userData.lastName}`
            : userData?.companyName 
            ? userData.companyName 
            : userData?.username || 'Unknown User';

          await addDoc(collection(db, 'notifications'), {
            type: 'like',
            fromUserId: currentUserId,
            fromUserName: userName,
            toUserId: post.authorId,
            postId: post.id,
            read: false,
            createdAt: new Date()
          });
        }
      }

      // Update local state based on the new like status
      setLikeCount(isLiked ? likeCount - 1 : likeCount + 1);
      setIsLiked(!isLiked);
    } catch (error) {
      console.error('Error updating like:', error);
    }
  };

  const formatDate = (timestamp: any) => {
    let date;
    if (timestamp?.toDate) {
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      date = new Date(timestamp);
    }
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  // Zoom functionality
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev * 1.5, 5));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev / 1.5, 0.5));
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
    setImagePosition({ x: 0, y: 0 });
  };

  const handleMouseWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoomLevel(prev => Math.max(0.5, Math.min(5, prev * delta)));
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - imagePosition.x, y: e.clientY - imagePosition.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoomLevel > 1) {
      setImagePosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1 && zoomLevel > 1) {
      const touch = e.touches[0];
      setIsDragging(true);
      setDragStart({ x: touch.clientX - imagePosition.x, y: touch.clientY - imagePosition.y });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isDragging && e.touches.length === 1 && zoomLevel > 1) {
      e.preventDefault();
      const touch = e.touches[0];
      setImagePosition({
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Reset zoom when modal closes
  const handleCloseModal = () => {
    setSelectedImage(null);
    handleResetZoom();
  };

  return (
    <IonCard className="animate-slideUp" style={{ 
      marginBottom: '20px', 
      opacity: isReady ? 1 : 0,
      transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
      overflow: 'hidden'
    }}>
      {/* Author Section */}
      <Box 
        sx={{ 
          p: 2.5, 
          display: 'flex', 
          alignItems: 'center',
          cursor: 'pointer',
          borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
          background: 'rgba(255, 255, 255, 0.5)'
        }}
        onClick={navigateToProfile}
      >
        <Box sx={{ mr: 2, width: 56, height: 56, position: 'relative' }}>
          {!profileImageLoaded && post.authorPhotoURL && (
            <CircularProgress size={48} sx={{ position: 'absolute', top: 0, left: 0 }} />
          )}
          {post.authorPhotoURL ? (
            <Avatar
              src={post.authorPhotoURL}
              alt={t('post.author_avatar_alt', { name: post.authorName })}
              sx={{
                width: 56,
                height: 56,
                display: profileImageLoaded ? 'block' : 'none',
                border: '2px solid',
                borderColor: 'primary.light',
                boxShadow: '0 4px 12px rgba(0, 104, 185, 0.15)'
              }}
              imgProps={{
                onLoad: () => setProfileImageLoaded(true),
                onError: () => setProfileImageLoaded(true) // Handle image load errors
              }}
            />
          ) : (
            <Box
              component="img"
              src={
                authorType === 'company' ? '/assets/image/types/Entreprise.png' :
                authorType === 'contractor' ? '/assets/image/types/Prestataire.png' :
                authorType === 'ambassador' ? '/assets/image/types/Affiliateur.png' :
                '/assets/image/logo.png'
              }
              alt={t('post.author_avatar_alt', { name: post.authorName })}
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                border: '2px solid',
                borderColor: 'primary.light',
                boxShadow: '0 4px 12px rgba(0, 104, 185, 0.15)',
                objectFit: 'cover'
              }}
            />
          )}
        </Box>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            {post.authorName}
          </Typography>
          {authorType && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title={t(`auth.${authorType}`)}>
                  <Chip
                    size="small"
                    label={t(`auth.${authorType}`)}
                    avatar={
                      <Avatar
                        src={
                          authorType === 'company' ? '/assets/image/types/Entreprise.png' :
                          authorType === 'contractor' ? '/assets/image/types/Prestataire.png' :
                          authorType === 'ambassador' ? '/assets/image/types/Affiliateur.png' :
                          '/assets/image/logo.png'
                        }
                        sx={{
                          width: 18,
                          height: 18
                        }}
                      />
                    }
                    sx={{
                      background: 'rgba(0, 104, 185, 0.1)',
                      color: 'primary.main',
                      fontWeight: 500,
                      fontSize: '0.7rem',
                      height: '22px'
                    }}
                  />
                </Tooltip>
              </Box>
              
              {authorSpecialization && (authorType === 'company' || authorType === 'contractor' || authorType === 'ambassador') && (
                <Chip
                  size="small"
                  label={authorSpecialization}
                  sx={{
                    background: 'rgba(145, 213, 112, 0.2)',
                    color: '#2e7d32',
                    fontWeight: 500,
                    fontSize: '0.7rem',
                    height: '22px',
                    maxWidth: '200px',
                    mt: 0.5,
                    '& .MuiChip-label': {
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'normal',
                      lineHeight: 1.2,
                      px: 1
                    }
                  }}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Title Section */}
      <Box sx={{ p: 2.5, cursor: 'pointer' }} onClick={() => history.push(`/post/${post.id}`)}>
        <Typography
          variant="h6"
          sx={{
            mb: 1.5,
            fontWeight: 'bold',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: '1.3',
            transition: 'all 0.3s ease',
            color: '#0068B9',
            '&:hover': {
              transform: 'translateY(-2px)'
            }
          }}
        >
          {post.title}
        </Typography>
        
        {/* Description Preview */}
        <Typography
          variant="body1"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            mb: 2,
            color: 'text.secondary',
            lineHeight: '1.6'
          }}
        >
          {post.description}
        </Typography>
      </Box>

      {/* Images Section */}
      {post.images.length > 0 && (
        <Swiper
          modules={[Pagination]}
          pagination={{ clickable: true }}
          style={{ maxHeight: '400px' }}
        >
          {post.images.map((image, index) => (
            <SwiperSlide key={index}>
              <Box sx={{ position: 'relative' }}>
                {!imagesLoaded[index] && (
                  <CircularProgress size={64} sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)'
                  }} />
                )}
                <img
                  src={image}
                  alt={t('post.image_alt', { index: index + 1 })}
                  onLoad={() => setImagesLoaded(prev => ({ ...prev, [index]: true }))}
                  style={{
                    width: '100%',
                    height: '400px',
                    objectFit: 'cover',
                    display: imagesLoaded[index] ? 'block' : 'none'
                  }}
                />
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedImage(image);
                  }}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'rgba(0, 0, 0, 0.3)',
                    backdropFilter: 'blur(4px)',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.5)',
                      transform: 'scale(1.1)'
                    },
                    transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
                  }}
                >
                  {isReady && <IonIcon icon={expand} style={{ color: 'white' }} />}
                </IconButton>
              </Box>
            </SwiperSlide>
          ))}
        </Swiper>
      )}

      <IonCardContent>
        {/* Likes and Date Section */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          my: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              onClick={handleLike} 
              size="small" 
              color="primary"
              className={isLiked ? 'animate-pulse' : ''}
              sx={{
                transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                '&:hover': {
                  transform: 'scale(1.2)'
                }
              }}
            >
              {isReady && <IonIcon icon={isLiked ? heart : heartOutline} style={{ fontSize: '22px' }} />}
            </IconButton>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {t('post.likes_count', { count: likeCount })}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {t('post.posted_on', { date: formatDate(post.createdAt) })}
          </Typography>
        </Box>


        {/* Show Post Link */}
        <Box 
          sx={{ 
            textAlign: 'center',
            cursor: 'pointer'
          }}
          onClick={() => history.push(`/post/${post.id}`)}
        >
          <Chip
            label={t('post.view_full_post', { defaultValue: 'View full post' })}
            color="primary"
            variant="outlined"
            sx={{ 
              fontWeight: 'medium',
              transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0, 104, 185, 0.2)'
              }
            }}
          />
        </Box>
      </IonCardContent>

      {/* Improved Image Gallery Modal */}
      <Modal
        open={!!selectedImage}
        onClose={handleCloseModal}
        closeAfterTransition
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(5px)'
        }}
      >
        <Fade in={!!selectedImage}>
          <Box sx={{
            width: '100%',
            height: '100%',
            bgcolor: 'rgba(0, 0, 0, 0.95)',
            outline: 'none',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
          }}>
            {/* Close button and zoom controls */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              zIndex: 1300
            }}>
              {/* Zoom controls */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton
                  onClick={handleZoomOut}
                  disabled={zoomLevel <= 0.5}
                  sx={{
                    color: 'white',
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:disabled': {
                      bgcolor: 'rgba(0, 0, 0, 0.3)',
                      color: 'rgba(255, 255, 255, 0.5)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                    <line x1="8" y1="11" x2="14" y2="11"></line>
                  </svg>
                </IconButton>
                <IconButton
                  onClick={handleResetZoom}
                  sx={{
                    color: 'white',
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                    fontSize: '0.75rem',
                    minWidth: 'auto',
                    px: 1,
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  {Math.round(zoomLevel * 100)}%
                </IconButton>
                <IconButton
                  onClick={handleZoomIn}
                  disabled={zoomLevel >= 5}
                  sx={{
                    color: 'white',
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:disabled': {
                      bgcolor: 'rgba(0, 0, 0, 0.3)',
                      color: 'rgba(255, 255, 255, 0.5)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                    <line x1="8" y1="11" x2="14" y2="11"></line>
                    <line x1="11" y1="8" x2="11" y2="14"></line>
                  </svg>
                </IconButton>
              </Box>
              
              {/* Close button */}
              <IconButton
                onClick={handleCloseModal}
                sx={{
                  color: 'white',
                  bgcolor: 'rgba(0, 0, 0, 0.5)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    transform: 'scale(1.1)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </IconButton>
            </Box>
            
            {/* Image counter */}
            {post.images.length > 1 && (
              <Box sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                zIndex: 1300,
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                px: 2,
                py: 0.5,
                borderRadius: 4,
                fontSize: '0.875rem',
                fontWeight: 'medium'
              }}>
                {post.images.findIndex(img => img === selectedImage) + 1} / {post.images.length}
              </Box>
            )}
            
            {/* Main Swiper Gallery */}
            <Box sx={{ 
              flex: 1, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              width: '100%',
              height: '100%'
            }}>
              <Swiper
                modules={[Pagination]}
                pagination={{ 
                  clickable: true,
                  dynamicBullets: true,
                  dynamicMainBullets: 3
                }}
                loop={post.images.length > 1}
                initialSlide={Math.max(0, post.images.findIndex(img => img === selectedImage))}
                style={{ 
                  width: '100%', 
                  height: '100%',
                  '--swiper-pagination-color': '#fff',
                  '--swiper-pagination-bullet-size': '10px',
                  '--swiper-pagination-bullet-inactive-color': '#999',
                  '--swiper-pagination-bullet-inactive-opacity': '0.5'
                } as any}
                spaceBetween={30}
                speed={500}
                grabCursor={true}
              >
                {post.images.map((image, index) => (
                  <SwiperSlide key={index}>
                    <Box sx={{
                      width: '100%',
                      height: '100vh',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: { xs: 2, sm: 4 }
                    }}>
                      <img 
                        src={image} 
                        alt={t('post.image_alt', { index: index + 1 })}
                        onWheel={handleMouseWheel}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        style={{ 
                          maxWidth: '100%',
                          maxHeight: '85vh',
                          objectFit: 'contain',
                          borderRadius: '8px',
                          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.3)',
                          transform: `scale(${zoomLevel}) translate(${imagePosition.x / zoomLevel}px, ${imagePosition.y / zoomLevel}px)`,
                          transformOrigin: 'center center',
                          transition: isDragging ? 'none' : 'transform 0.3s ease',
                          cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                          userSelect: 'none',
                          touchAction: 'none'
                        }}
                      />
                    </Box>
                  </SwiperSlide>
                ))}
                
                {/* Navigation arrows for larger screens */}
                {post.images.length > 1 && (
                  <>
                    <Box 
                      className="custom-swiper-button-prev" 
                      onClick={(e) => {
                        const swiper = (e.currentTarget.closest('.swiper') as any)?.swiper;
                        if (swiper) swiper.slidePrev();
                      }}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: { xs: '5px', sm: '20px' },
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                        width: { xs: '40px', sm: '50px' },
                        height: { xs: '40px', sm: '50px' },
                        borderRadius: '50%',
                        background: 'rgba(0, 0, 0, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        color: 'white',
                        '&:hover': {
                          background: 'rgba(0, 0, 0, 0.5)',
                        }
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 18 9 12 15 6"></polyline>
                      </svg>
                    </Box>
                    <Box 
                      className="custom-swiper-button-next" 
                      onClick={(e) => {
                        const swiper = (e.currentTarget.closest('.swiper') as any)?.swiper;
                        if (swiper) swiper.slideNext();
                      }}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        right: { xs: '5px', sm: '20px' },
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                        width: { xs: '40px', sm: '50px' },
                        height: { xs: '40px', sm: '50px' },
                        borderRadius: '50%',
                        background: 'rgba(0, 0, 0, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        color: 'white',
                        '&:hover': {
                          background: 'rgba(0, 0, 0, 0.5)',
                        }
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </Box>
                  </>
                )}
              </Swiper>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </IonCard>
  );
};
